import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { portalLinks } from '../config/portalLinks';
import { SITE_NAME } from '../config/siteBranding';
import {
  DAILY_SPORTS_LIFE_WWW,
  isDailySportsLifeHost,
  VERCEL_PRODUCTION_ORIGIN,
} from '../utils/canonicalOrigin';
import { PORTAL_HERO_IMAGE } from '../config/portalLandingAssets';
import PortalClubsSection from './PortalClubsSection';
import PlayerHomeDashboard from './PlayerHomeDashboard';
import TrainingTodayCard from './TrainingTodayCard';
import MembershipStatusBanner, { useMyClubMemberships } from './MembershipStatusBanner';
import AppSwitcher from './AppSwitcher';
import { soccerPath, accountPath } from '../utils/paths';
import './PortalHome.css';
import './MembershipStatusBanner.css';

const PortalHome = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { pendingMemberships } = useMyClubMemberships(isAuthenticated ? user?.id : null);

  return (
    <div className="portal-home">
      <section className="portal-hero-band" aria-label={`${SITE_NAME} home`}>
        <div
          className="portal-hero-band__media"
          style={{ backgroundImage: `url(${PORTAL_HERO_IMAGE})` }}
          role="img"
          aria-label="People training and playing sports outdoors"
        />
        <div className="portal-hero-band__shade" aria-hidden="true" />

        <header className="portal-topbar portal-topbar--on-hero">
          <Link to="/" className="portal-topbar__brand">
            {SITE_NAME}
          </Link>
          <nav className="portal-topbar__nav" aria-label="Account">
            {isAuthenticated ? (
              <>
                <Link to={accountPath()} className="portal-topbar__text-link">
                  Account
                </Link>
                <span className="portal-topbar__user">Hi, {user?.name?.split(' ')[0] || 'there'}</span>
                <button type="button" className="portal-topbar__btn" onClick={logout}>
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="portal-topbar__text-link">
                  Log in
                </Link>
                <Link to="/register" className="portal-topbar__btn portal-topbar__btn--primary">
                  Join
                </Link>
              </>
            )}
          </nav>
        </header>

        <div className="portal-hero-band__content">
          <h1 className="portal-hero-band__brand">{SITE_NAME}</h1>
          <p className="portal-hero-band__tagline">
            Tools and stories to play, train, and grow with your community.
          </p>
          <div className="portal-hero-band__ctas">
            <Link to="/soccer" className="btn btn-primary portal-hero-band__cta">
              Open Soccer
            </Link>
            <Link to="/running" className="btn portal-hero-band__cta portal-hero-band__cta--ghost">
              Open Running
            </Link>
            <Link to="/hiking" className="btn portal-hero-band__cta portal-hero-band__cta--ghost">
              Open Hiking
            </Link>
            <Link to="/training" className="btn portal-hero-band__cta portal-hero-band__cta--ghost">
              Start Training
            </Link>
          </div>
          <div className="portal-hero-band__apps">
            <AppSwitcher variant="onDark" />
          </div>
        </div>
      </section>

      <div className="portal-home__body">
        {isAuthenticated && pendingMemberships.length > 0 && (
          <div className="portal-page__section">
            <MembershipStatusBanner pendingMemberships={pendingMemberships} variant="compact" />
            <p className="portal-page__note">
              <Link to={soccerPath('/clubs/list')}>View club request status →</Link>
            </p>
          </div>
        )}

        {isAuthenticated && user?.id && (
          <div className="portal-page__section">
            <TrainingTodayCard userId={user.id} />
            <PlayerHomeDashboard userId={user.id} limit={10} />
          </div>
        )}

        <section className="portal-apps" aria-label="Daily Sports Life apps">
          <div className="portal-apps__intro">
            <h2>Your apps</h2>
            <p>Soccer, Running, Training, and Blog — everything in one place.</p>
          </div>
          <div className="portal-grid">
            {portalLinks.map((item) => {
              const cardClass = `portal-card${item.available ? '' : ' portal-card--muted'}`;

              const body = (
                <>
                  <span className="portal-card-icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  {item.comingSoon ? (
                    <span className="portal-card-cta portal-card-cta--disabled">Coming soon</span>
                  ) : (
                    <span className="portal-card-cta">Open app →</span>
                  )}
                </>
              );

              if (!item.available) {
                return (
                  <article key={item.id} className={cardClass}>
                    {body}
                  </article>
                );
              }

              if (item.internal) {
                return (
                  <Link key={item.id} to={item.href} className={cardClass}>
                    {body}
                  </Link>
                );
              }

              return (
                <a
                  key={item.id}
                  href={item.href}
                  className={cardClass}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {body}
                </a>
              );
            })}
          </div>
        </section>

        <PortalClubsSection />

        <footer className="portal-footer">
          {isDailySportsLifeHost() && (
            <p className="portal-connectivity-help">
              Trouble loading on your Wi-Fi? Try{' '}
              <a href={VERCEL_PRODUCTION_ORIGIN}>{VERCEL_PRODUCTION_ORIGIN.replace('https://', '')}</a>
              {' '}or{' '}
              <a href={DAILY_SPORTS_LIFE_WWW}>www.dailysportslife.com</a>
            </p>
          )}
          <p>
            © {new Date().getFullYear()} {SITE_NAME}
          </p>
        </footer>
      </div>
    </div>
  );
};

export default PortalHome;
